import { Controller } from "@hotwired/stimulus"
import { shortcutBlocked, isTypingTarget } from "../lib/shortcut_guard"

// Keyboard control of the test runner (courses/test_practices/show), on the page
// container: it outlives the `test-results-form` frame swaps and holds both the header
// (the shortcut list) and the frame (the navigator). Key ownership:
//   - 1–9: checkbox_controller (it owns the answer state); seen here only to mark the
//     keyboard as discovered.
//   - Enter, → (← under RTL): forward — submit the answer on the answering screen, or
//     Next/Finish on the review screen. ← (→ under RTL): Back. ?: the shortcut list.
// Controls are found through the navigator's `data-nav-role` contract (as swipe_nav
// does) on every press, so whichever screen the frame holds right now is what acts.
//
// Nudge: a desktop learner who keeps answering with the mouse gets the shortcut list
// opened once, after the 3rd mouse-answered question, when the next screen renders.
// Anyone who presses a shortcut (or opens the list) has found it and never gets it.
// Off for timed attempts (nudge value) — no pop-ups against the clock.
//
// Usage analytics: the answers form's hidden input_method says how each answer was
// given (keyboard / mouse / touch / pen → Question Answered); the list being opened
// and keyboard moves between questions are beaconed to Courses::KeyboardEventsController.
const NUDGE_AFTER = 3
const NUDGE_TOOLTIP_MS = 1500
const SEEN_KEY = "keyboardHintSeen"
const COUNT_KEY = "keyboardHintMouseAnswers"
const INTERACTIVE = "a[href], button, summary, [role=button]"

export default class extends Controller {
  static targets = ["help"]
  static values = { nudge: Boolean, trackUrl: String, testId: Number, blockId: Number }

  // Bound here, not in connect(): Stimulus runs helpTargetConnected before connect().
  initialize() {
    this.boundKeydown = this.keydown.bind(this)
    this.boundPointerdown = this.pointerdown.bind(this)
    this.boundFrameLoad = this.frameLoad.bind(this)
    this.boundHelpToggle = this.helpToggled.bind(this)
  }

  connect() {
    this.nudgePending = false
    this.countedForms = new Set()

    document.addEventListener("keydown", this.boundKeydown)
    this.element.addEventListener("pointerdown", this.boundPointerdown)
    this.element.addEventListener("turbo:frame-load", this.boundFrameLoad)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    this.element.removeEventListener("pointerdown", this.boundPointerdown)
    this.element.removeEventListener("turbo:frame-load", this.boundFrameLoad)
    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout)
  }

  helpTargetConnected(element) {
    element.addEventListener("toggle", this.boundHelpToggle)
  }

  helpTargetDisconnected(element) {
    element.removeEventListener("toggle", this.boundHelpToggle)
  }

  keydown(event) {
    if (shortcutBlocked(event)) return
    const key = event.key
    const target = event.target

    if (key === "Enter") {
      this.enter(event)
    } else if (key === "ArrowRight" || key === "ArrowLeft") {
      // Caret moves in a text answer; a focused select changes its value.
      if (isTypingTarget(target)) return
      const forwardKey = this.isRtl ? "ArrowLeft" : "ArrowRight"
      if (key === forwardKey) this.forward(event)
      else this.back(event)
    } else if (key === "?") {
      if (isTypingTarget(target) || !this.hasHelpTarget) return
      event.preventDefault()
      this.helpSource = "key"
      this.toggleHelp()
    } else if (/^[1-9]$/.test(key) && !isTypingTarget(target) && this.answerForm) {
      this.markSeen()
    }
  }

  // Enter on a focused link or button is the browser's: it activates that control
  // (Back, the star, Restart). Navigating as well would act twice. After a digit or an
  // answer click the focus sits on Next itself, so the native activation submits.
  enter(event) {
    const focused = event.target && event.target.closest && event.target.closest(INTERACTIVE)
    if (focused) {
      if (focused === this.forwardControl && !focused.disabled && !this.inFlight(focused)) {
        this.markKeyboard(focused, "enter")
      }
      return
    }
    this.forward(event)
  }

  forward(event) {
    const control = this.forwardControl
    if (!control) return
    // A request in flight (checkbox#lockForm, next_link) is not "nothing picked", and
    // pressing again must not count as another move.
    if (this.inFlight(control)) return

    if (control.disabled) {
      // A text answer still being typed keeps its newline.
      if (isTypingTarget(event.target)) return
      event.preventDefault()
      this.showAnswerFirst(control)
      return
    }

    event.preventDefault()
    this.markKeyboard(control, event.key === "Enter" ? "enter" : "arrow")
    // checkbox#guardSubmit still stops an empty or repeated submit.
    control.click()
  }

  back(event) {
    const control = this.navEl("prev")
    if (!control) return
    event.preventDefault()
    this.markSeen()
    this.track("navigated", { control: "back", key: "arrow", screen: this.answerForm ? "answering" : "review" })
    control.click()
  }

  // The answering screen's Next is the answers form's submit button; the review screen's
  // is a link to the next question, or Finish on the last one.
  get forwardControl() {
    return this.navEl("next") || this.navEl("finish")
  }

  navEl(role) {
    return this.element.querySelector(`[data-nav-role="${role}"]`)
  }

  inFlight(control) {
    return control.getAttribute("aria-busy") === "true"
  }

  // The unanswered question's form, while the answering screen is on.
  get answerForm() {
    return this.element.querySelector('form input[name="input_method"]')?.form || null
  }

  get isRtl() {
    return getComputedStyle(this.element).direction === "rtl"
  }

  // Flash the locked Next's "answer first" tooltip (normally shown on hover).
  showAnswerFirst(control) {
    const lock = control.closest(".next-lock")
    if (!lock) return
    lock.classList.add("is-nudged")
    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout)
    this.tooltipTimeout = setTimeout(() => lock.classList.remove("is-nudged"), NUDGE_TOOLTIP_MS)
  }

  // A keyboard move forward: an answer sent (tagged on its Question Answered event)
  // or, on the review screen, a Next/Finish link followed (beaconed).
  markKeyboard(control, key) {
    this.markSeen()
    const form = control.form
    if (form && form.elements.input_method) {
      form.elements.input_method.value = "keyboard"
    } else {
      const role = control.dataset.navRole === "finish" ? "finish" : "next"
      this.track("navigated", { control: role, key, screen: "review" })
    }
  }

  toggleHelp() {
    if (typeof this.helpTarget.togglePopover === "function") this.helpTarget.togglePopover()
  }

  // Opened by `?` or the nudge (they set helpSource first), else by the header button.
  helpToggled(event) {
    const source = this.helpSource || "button"
    this.helpSource = null
    if (event.newState !== "open") {
      // Closing leaves focus on (or returns it to) the header button, and Enter on a
      // focused button is the browser's: it would reopen the list instead of going on.
      const active = document.activeElement
      if (active && active.closest(`[popovertarget="${this.helpTarget.id}"]`)) active.blur()
      return
    }
    this.markSeen()
    this.track("help_opened", { source })
  }

  track(event, properties) {
    if (!this.hasTrackUrlValue) return
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.trackUrlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ event, ...properties, test_id: this.testIdValue, block_id: this.blockIdValue }),
      // Finish leaves the page: let the beacon outlive it.
      keepalive: true,
    }).catch(() => {})
  }

  // ── Nudge and answer input ──

  // A pointer picking an answer option: tag the answer with the pointer type (unless a
  // shortcut already made it a keyboard answer), and count mouse answers for the nudge.
  pointerdown(event) {
    const label = event.target.closest("label.answer-option")
    const form = label && label.closest("form")
    if (!form) return

    const field = form.elements.input_method
    if (field && field.value !== "keyboard" && event.pointerType) field.value = event.pointerType

    if (event.pointerType !== "mouse" || this.nudgePending || !this.nudgeEnabled) return
    if (this.countedForms.has(form.id)) return
    this.countedForms.add(form.id)

    const count = this.storedCount() + 1
    this.store(COUNT_KEY, String(count))
    if (count >= NUDGE_AFTER) this.nudgePending = true
  }

  // The next runner screen has rendered: open the list over it. Not over the results
  // redirect the last answer returns (no navigator in it) — wait for a real screen.
  frameLoad() {
    if (!this.nudgePending || !this.forwardControl) return
    this.nudgePending = false
    if (!this.nudgeEnabled || typeof this.helpTarget.showPopover !== "function") return
    this.markSeen()
    this.helpSource = "nudge"
    this.helpTarget.showPopover()
  }

  get nudgeEnabled() {
    return this.nudgeValue && this.hasHelpTarget && !this.seen &&
      window.matchMedia("(pointer: fine)").matches
  }

  get seen() {
    return this.read(SEEN_KEY) === "1"
  }

  markSeen() {
    if (!this.seen) this.store(SEEN_KEY, "1")
  }

  storedCount() {
    return parseInt(this.read(COUNT_KEY), 10) || 0
  }

  // localStorage can throw (private mode, blocked site data): the nudge then simply
  // never fires, and the shortcuts work regardless.
  read(key) {
    try { return window.localStorage.getItem(key) } catch (_e) { return null }
  }

  store(key, value) {
    try { window.localStorage.setItem(key, value) } catch (_e) { /* see read */ }
  }
}
